// ABOUTME: Admin policy management component for editing privacy policy and terms of service
// ABOUTME: Provides Markdown editor with live preview and version tracking

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  Chip,
  Container,
  Collapse,
  IconButton,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Save,
  History,
  Edit,
  Visibility,
  ExpandMore,
  ExpandLess,
  Download,
  ArrowBack
} from '@mui/icons-material';
import * as api from '../services/api';
import { renderPolicyMarkdown } from '../utils/sanitize';

interface PolicyDocument {
  id: number;
  documentType: 'PrivacyPolicy' | 'TermsOfService';
  content: string;
  version: number;
  versionLabel?: string;
  lastModified: string;
  modifiedByUserId: number;
  modifiedByUserName: string;
}

interface PolicyDocumentHistory {
  id: number;
  documentId: number;
  documentType: 'PrivacyPolicy' | 'TermsOfService';
  content: string;
  version: number;
  versionLabel?: string;
  changeReason?: string;
  createdAt: string;
  createdByUserId: number;
  createdByUserName: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const AdminPolicyManagement: React.FC = () => {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const [editMode, setEditMode] = useState<'edit' | 'preview'>('edit');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Policy documents state
  const [privacyPolicy, setPrivacyPolicy] = useState<PolicyDocument | null>(null);
  const [termsOfService, setTermsOfService] = useState<PolicyDocument | null>(null);
  
  // Edit form state
  const [editContent, setEditContent] = useState('');
  const [versionLabel, setVersionLabel] = useState('');
  const [changeReason, setChangeReason] = useState('');
  const [sendNotification, setSendNotification] = useState(false);
  
  // History dialog state
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState<PolicyDocumentHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistoryId, setExpandedHistoryId] = useState<number | null>(null);

  const getPolicyType = () => selectedTab === 0 ? 'PrivacyPolicy' : 'TermsOfService';
  const getPolicyName = () => selectedTab === 0 ? 'Privacy Policy' : 'Terms of Service';

  const loadPolicies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [privacy, terms] = await Promise.all([
        api.adminGetPrivacyPolicy(),
        api.adminGetTermsOfService()
      ]);
      
      setPrivacyPolicy(privacy);
      setTermsOfService(terms);
      
      // Initialize edit form with current policy
      const currentPolicy = selectedTab === 0 ? privacy : terms;
      setEditContent(currentPolicy.content);
      setVersionLabel('');
      setChangeReason('');
      setSendNotification(false);
      
    } catch (err) {
      setError((axios.isAxiosError(err) ? err.response?.data?.message : undefined) || 'Error loading policies');
    } finally {
      setLoading(false);
    }
  }, [selectedTab]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    setEditMode('edit');
    setError(null);
    setSuccess(null);
    
    // Reset form with new policy content
    const newPolicy = newValue === 0 ? privacyPolicy : termsOfService;
    if (newPolicy) {
      setEditContent(newPolicy.content);
      setVersionLabel('');
      setChangeReason('');
      setSendNotification(false);
    }
  };

  const handleSave = async () => {
    if (!editContent.trim()) {
      setError('Content cannot be empty');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      const updateData = {
        content: editContent,
        versionLabel: versionLabel || undefined,
        changeReason: changeReason || undefined,
        sendNotification: sendNotification
      };

      let updatedPolicy: PolicyDocument;
      if (selectedTab === 0) {
        updatedPolicy = await api.adminUpdatePrivacyPolicy(updateData);
        setPrivacyPolicy(updatedPolicy);
      } else {
        updatedPolicy = await api.adminUpdateTermsOfService(updateData);
        setTermsOfService(updatedPolicy);
      }

      setSuccess(`${getPolicyName()} updated successfully to version ${updatedPolicy.version}`);
      setVersionLabel('');
      setChangeReason('');
      setSendNotification(false);
      
    } catch (err) {
      setError((axios.isAxiosError(err) ? err.response?.data?.message : undefined) || 'Error saving policy');
    } finally {
      setSaving(false);
    }
  };

  const handleViewHistory = async () => {
    try {
      setHistoryLoading(true);
      const history = await api.adminGetPolicyHistory(getPolicyType());
      setHistoryData(history);
      setHistoryOpen(true);
      setExpandedHistoryId(null); // Reset expanded state
    } catch (err) {
      setError((axios.isAxiosError(err) ? err.response?.data?.message : undefined) || 'Error loading history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleHistoryExpansion = (historyId: number) => {
    setExpandedHistoryId(expandedHistoryId === historyId ? null : historyId);
  };

  const downloadPolicyVersion = (item: PolicyDocumentHistory) => {
    const policyTypeName = item.documentType === 'PrivacyPolicy' ? 'Privacy-Policy' : 'Terms-of-Service';
    const versionPart = `v${item.version}`;
    const labelPart = item.versionLabel ? `-${item.versionLabel.replace(/[^a-zA-Z0-9.-]/g, '-')}` : '';
    const filename = `${policyTypeName}-${versionPart}${labelPart}.md`;
    
    const blob = new Blob([item.content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderMarkdown = renderPolicyMarkdown;

  useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      {/* Header with back button */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin')}
          sx={{ mr: 2 }}
        >
          Back to Admin
        </Button>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
            Policy Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage privacy policy and terms of service documents with version control.
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Privacy Policy" />
          <Tab label="Terms of Service" />
        </Tabs>
      </Paper>

      <TabPanel value={selectedTab} index={0}>
        <PolicyEditor
          policy={privacyPolicy}
          editMode={editMode}
          editContent={editContent}
          versionLabel={versionLabel}
          changeReason={changeReason}
          sendNotification={sendNotification}
          saving={saving}
          onEditModeChange={setEditMode}
          onContentChange={setEditContent}
          onVersionLabelChange={setVersionLabel}
          onChangeReasonChange={setChangeReason}
          onSendNotificationChange={setSendNotification}
          onSave={handleSave}
          onViewHistory={handleViewHistory}
          renderMarkdown={renderMarkdown}
        />
      </TabPanel>

      <TabPanel value={selectedTab} index={1}>
        <PolicyEditor
          policy={termsOfService}
          editMode={editMode}
          editContent={editContent}
          versionLabel={versionLabel}
          changeReason={changeReason}
          sendNotification={sendNotification}
          saving={saving}
          onEditModeChange={setEditMode}
          onContentChange={setEditContent}
          onVersionLabelChange={setVersionLabel}
          onChangeReasonChange={setChangeReason}
          onSendNotificationChange={setSendNotification}
          onSave={handleSave}
          onViewHistory={handleViewHistory}
          renderMarkdown={renderMarkdown}
        />
      </TabPanel>

      {/* History Dialog */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {getPolicyName()} History
        </DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {historyData.map((item) => (
                <Box key={item.id}>
                  <ListItem 
                    divider 
                    sx={{ 
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <Box 
                      display="flex" 
                      width="100%" 
                      alignItems="center" 
                      onClick={() => toggleHistoryExpansion(item.id)}
                    >
                      <Box flexGrow={1}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Typography variant="subtitle1">
                            Version {item.version}
                          </Typography>
                          {item.versionLabel && (
                            <Chip label={item.versionLabel} size="small" variant="outlined" />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Updated by {item.createdByUserName} on{' '}
                          {new Date(item.createdAt).toLocaleDateString()}
                        </Typography>
                        {item.changeReason && (
                          <Typography variant="body2" color="text.secondary">
                            Reason: {item.changeReason}
                          </Typography>
                        )}
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadPolicyVersion(item);
                          }}
                          title="Download this version"
                        >
                          <Download />
                        </IconButton>
                        <IconButton size="small">
                          {expandedHistoryId === item.id ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </Box>
                    </Box>
                    
                    <Collapse in={expandedHistoryId === item.id}>
                      <Box sx={{ mt: 2, mb: 1 }}>
                        <Paper 
                          sx={{ 
                            p: 2, 
                            bgcolor: 'grey.50', 
                            maxHeight: 400, 
                            overflow: 'auto',
                            border: '1px solid',
                            borderColor: 'divider'
                          }}
                        >
                          <div
                            dangerouslySetInnerHTML={{
                              __html: renderMarkdown(item.content)
                            }}
                          />
                        </Paper>
                      </Box>
                    </Collapse>
                  </ListItem>
                </Box>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

interface PolicyEditorProps {
  policy: PolicyDocument | null;
  editMode: 'edit' | 'preview';
  editContent: string;
  versionLabel: string;
  changeReason: string;
  sendNotification: boolean;
  saving: boolean;
  onEditModeChange: (mode: 'edit' | 'preview') => void;
  onContentChange: (content: string) => void;
  onVersionLabelChange: (label: string) => void;
  onChangeReasonChange: (reason: string) => void;
  onSendNotificationChange: (sendNotification: boolean) => void;
  onSave: () => void;
  onViewHistory: () => void;
  renderMarkdown: (content: string) => string;
}

const PolicyEditor: React.FC<PolicyEditorProps> = ({
  policy,
  editMode,
  editContent,
  versionLabel,
  changeReason,
  sendNotification,
  saving,
  onEditModeChange,
  onContentChange,
  onVersionLabelChange,
  onChangeReasonChange,
  onSendNotificationChange,
  onSave,
  onViewHistory,
  renderMarkdown
}) => {
  const hasChanges = policy ? editContent !== policy.content : false;

  // Reset notification checkbox when content is reverted to original
  React.useEffect(() => {
    if (policy && !hasChanges && sendNotification) {
      onSendNotificationChange(false);
    }
  }, [policy, hasChanges, sendNotification, onSendNotificationChange]);

  if (!policy) return null;

  return (
    <Box>
      {/* Current Version Info */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
        <Typography variant="h6" gutterBottom>
          Current Version: {policy.version}
          {policy.versionLabel && ` (${policy.versionLabel})`}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Last modified by {policy.modifiedByUserName} on{' '}
          {new Date(policy.lastModified).toLocaleDateString()}
        </Typography>
      </Paper>

      {/* Editor Controls */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <Button
          variant={editMode === 'edit' ? 'contained' : 'outlined'}
          startIcon={<Edit />}
          onClick={() => onEditModeChange('edit')}
        >
          Edit
        </Button>
        <Button
          variant={editMode === 'preview' ? 'contained' : 'outlined'}
          startIcon={<Visibility />}
          onClick={() => onEditModeChange('preview')}
        >
          Preview
        </Button>
        <Button
          variant="outlined"
          startIcon={<History />}
          onClick={onViewHistory}
        >
          View History
        </Button>
      </Box>

      {/* Edit Mode */}
      {editMode === 'edit' && (
        <Box>
          <TextField
            fullWidth
            multiline
            rows={20}
            variant="outlined"
            label="Content (Markdown)"
            value={editContent}
            onChange={(e) => onContentChange(e.target.value)}
            sx={{ mb: 2 }}
            placeholder="Enter policy content using Markdown formatting..."
          />
          
          <Box display="flex" gap={2} mb={2} flexWrap="wrap">
            <TextField
              label="Label (optional)"
              value={versionLabel}
              onChange={(e) => onVersionLabelChange(e.target.value)}
              placeholder="e.g., v2.0, 2024.1"
              sx={{ minWidth: 200 }}
            />
            <TextField
              label="Change Reason (optional)"
              value={changeReason}
              onChange={(e) => onChangeReasonChange(e.target.value)}
              placeholder="e.g., GDPR compliance update"
              sx={{ minWidth: 300 }}
            />
          </Box>

          {hasChanges && (
            <Box sx={{ mt: 2, mb: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendNotification}
                    onChange={(e) => onSendNotificationChange(e.target.checked)}
                  />
                }
                label={`Send email notification to all users about this ${policy?.documentType === 'PrivacyPolicy' ? 'Privacy Policy' : 'Terms of Service'} update`}
              />
            </Box>
          )}

          <Box sx={{ mt: 3, mb: 4 }}>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={onSave}
              disabled={!hasChanges || saving}
              sx={{ mr: 2 }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>

            {hasChanges && (
              <Typography variant="body2" color="warning.main">
                You have unsaved changes
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Preview Mode */}
      {editMode === 'preview' && (
        <Paper sx={{ p: 3, minHeight: 400 }}>
          <div
            dangerouslySetInnerHTML={{
              __html: renderMarkdown(editContent)
            }}
          />
        </Paper>
      )}
    </Box>
  );
};

export default AdminPolicyManagement;